'use client';

import clsx from 'clsx';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { Sparkle } from 'lucide-react';
import Link from 'next/link';
import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { CardGroup, ResourceSection } from '@context/explorer/types';
import { useFetchUser } from '@/auth/hooks';
import { explorerAnalytics } from '@/lib/analytics';

type CardSectionId = string;

/*
  CONSTANTS: The units here is REM's

  Reason for hard coded values:
  - support the card stack layout in a more predictable way / have a consistent layout
  - since the cards are absolutely positioned, with these values and offsets it's easier to position and also calculate the card stack's dimensions
*/
const CARD_HEIGHT = 10.125;
const CARD_WIDTH = 15.375;

const CARD_OFFSET_VERTICAL = 2.5;
const CARD_OFFSET_HORIZONTAL = 0.75;

// Minimised State of the Card when not hovered (turns into a pill)
const PILL_HEIGHT = 3.75;
const PILL_SPACING = 0.75;

/**
 * Renders Card stacks section wise
 * Eg. Customer card group, Subscription card group
 */
function EndpointSelector({
  activeResource,
  setActiveResource,
  operationGroups,
  scrolledTop,
}: {
  activeResource: string;
  setActiveResource: (id: string) => void;
  operationGroups: ResourceSection[];
  scrolledTop: boolean;
}) {
  useEffect(() => {
    if (window.scrollY === 0 || scrolledTop) {
      setActiveResource(operationGroups[0]?.id);
    }
  }, [operationGroups, setActiveResource]);
  return (
    <>
      {operationGroups &&
        operationGroups.map((group, index) => (
          <CardSection
            key={index}
            id={group.id}
            group={group.items}
            activeId={activeResource}
            setActiveId={(id) => setActiveResource(id)}
          />
        ))}
    </>
  );
}

/**
 * Renders the card stacks of the particular card section
 * Keeps track of the active section, by
 * - by default setting the first section (customers) as active
 * - when the card section reaches the top 40% of the viewport, set the visible section as active
 */
const CardSection = ({
  id,
  group,
  activeId,
  setActiveId,
}: {
  id: CardSectionId;
  group: CardGroup[];
  activeId: string;
  setActiveId: (id: string) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    margin: '0px 0px -60% 0px',
  });

  useLayoutEffect(() => {
    if (isInView) {
      setActiveId(id);
    }
  }, [isInView, id, setActiveId]);

  return (
    <CardStack isActive={activeId === id} id={id} refProp={ref} group={group} />
  );
};

/**
 * Renders the card stacks of the particular card group, (4 cards at max in a stack, currently set in the dummy data level)
 * Keeps track of the hovered card index for each card stack, other card index lower than the hovered card index will be rendered as a pill
 */
const CardStack = memo(function CardStack({
  group,
  refProp,
  id,
  isActive,
}: {
  group: CardGroup[];
  refProp: React.RefObject<HTMLDivElement | null>;
  id: string;
  isActive: boolean;
}) {
  const [hoveredCards, setHoveredCards] = useState<Record<number, number>>({});
  const handleGroupLeave = (groupId: string) => {
    setHoveredCards((prev) => ({
      ...prev,
      [groupId]: -1,
    }));
  };

  const handleCardHover = (groupId: string, index: number) => {
    setHoveredCards((prev) => ({
      ...prev,
      [groupId]: index,
    }));
  };

  return (
    <motion.div ref={refProp}>
      <ul className={clsx('flex flex-wrap gap-8 mb-28', !isActive && '')}>
        {group.map((groupItem) => {
          const style = {
            height: `calc(${CARD_HEIGHT}rem + 2rem * ${groupItem.cards.length})`,
            width: `calc(${CARD_WIDTH}rem + ${groupItem.cards.length} * ${CARD_OFFSET_HORIZONTAL}rem)`,
          };

          return (
            <li
              key={groupItem.id}
              className="relative scroll-mt-28"
              style={style}
              onMouseLeave={() => handleGroupLeave(String(groupItem.id))}
              id={id}
            >
              <AnimatePresence>
                {groupItem.cards.map((card, index: number) => (
                  <Card
                    resource_id={card.resource_id}
                    operation_id={card.operation_id}
                    key={index}
                    tag={card.tag}
                    title={card.title}
                    stack={index}
                    selectedIndex={hoveredCards[groupItem.id]}
                    isHovered={hoveredCards[groupItem.id] === index}
                    onHover={() => handleCardHover(String(groupItem.id), index)}
                  />
                ))}
              </AnimatePresence>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
});

/**
 * Renders a single card
 * - Card can be pill or stacked
 * - Card can be hovered
 */
const Card = memo(function Card({
  resource_id,
  operation_id,
  tag,
  title,
  stack,
  isHovered,
  selectedIndex,
  onHover,
}: {
  resource_id: string;
  operation_id: string;
  tag: string;
  title?: React.ReactElement;
  stack: number;
  isHovered?: boolean;
  selectedIndex: number;
  onHover?: () => void;
}) {
  const { isLoggedIn } = useFetchUser();
  const noCardsHovered = selectedIndex === -1;
  const isPilled = selectedIndex < stack;

  const getCardPosition = () => {
    if (noCardsHovered) {
      return {
        top: `calc(${CARD_OFFSET_VERTICAL}rem * ${stack})`,
        right: `calc(${CARD_OFFSET_HORIZONTAL}rem * ${stack})`,
        scale: 1,
        height: `${CARD_HEIGHT}rem`,
        borderRadius: '1.875rem',
      };
    }

    if (isPilled) {
      return {
        x: `calc(${-CARD_OFFSET_HORIZONTAL}rem * ${stack})`,
        y: `calc(${CARD_HEIGHT}rem - ${PILL_HEIGHT - PILL_SPACING}rem)`,
        scale: 1,
        height: `${PILL_HEIGHT}rem`,
        borderRadius: '1.075rem',
      };
    }

    return {
      top: `calc(${CARD_OFFSET_VERTICAL}rem * ${stack})`,
      right: `calc(${CARD_OFFSET_HORIZONTAL}rem * ${stack})`,
      scale: isHovered ? 1.05 : 1,
      height: `${CARD_HEIGHT}rem`,
      borderRadius: '1.875rem',
    };
  };

  const shouldShowContent = !isPilled || noCardsHovered;

  return (
    <motion.div
      className={`border border-[#a2c1c4] backdrop-filter backdrop-blur-[4px] bg-[linear-gradient(0deg,_hsla(0,_0%,_100%,_0.5),_hsla(0,_0%,_100%,_0.5)),_linear-gradient(222deg,_rgba(144,_194,_199,_0)_13.06%,_rgba(143,_204,_218,_0.2)_36.39%,_#d2f6fa_63.38%)] shadow-[0px_-6px_54px_0px_rgba(0,0,0,0.35)] absolute flex flex-col cursor-pointer overflow-hidden`}
      style={{
        width: `${CARD_WIDTH}rem`,
      }}
      initial={{
        top: `calc(${CARD_OFFSET_VERTICAL}rem * ${stack})`,
        right: `calc(${CARD_OFFSET_HORIZONTAL}rem * ${stack})`,
        scale: 1,
        borderRadius: '1.875rem',
      }}
      animate={getCardPosition()}
      onHoverStart={onHover}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        mass: 1.2,
        bounce: 0.1,
      }}
    >
      <Link
        href={`/${resource_id}/${operation_id}`}
        className="flex flex-col flex-1"
        onClick={() => {
          explorerAnalytics.endpointNavigated({
            resource: resource_id || '',
            endpoint: operation_id || '',
            httpMethod: '',
            navigationSource: 'api_card',
            isLoggedIn: !!isLoggedIn,
          });
        }}
      >
        {/* Truncated tag by default, fully visible on hover */}
        <motion.div
          className={clsx(
            'bg-gradient-to-r from-transparent to-shade-lime-500 inline-flex py-1.5 px-3 items-center gap-1 rounded-md text-[#012a38] text-xs font-bold tracking-tight mt-3 ml-[0.94rem] self-start font-inter max-w-52 overflow-hidden whitespace-nowrap text-ellipsis',
            isHovered && 'overflow-visible whitespace-normal max-w-none'
          )}
        >
          <Sparkle className="w-4 h-4 flex-shrink-0" />
          <span className={clsx('truncate', isHovered && 'whitespace-normal')}>
            {tag}
          </span>
        </motion.div>
        <motion.div
          className="mt-auto ml-[1.3rem] mb-[1.3rem] mr-[1.3rem] text-[1rem] leading-[1.3rem] text-[#012A38]"
          animate={{
            opacity: shouldShowContent ? 1 : 0,
          }}
          transition={{ duration: 0.2 }}
        >
          {title}
        </motion.div>
      </Link>
    </motion.div>
  );
});

export default EndpointSelector;
